import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc, increment, onSnapshot, runTransaction } from 'firebase/firestore';
import logger from '../utils/logger';
import { stockDeltas } from './serviceRecords.logic';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService } from './finesService';
import { storeError, writableFields } from './fleetStore';

const inventoryCollection = collection(db, 'fleet_service_inventory');
const serviceRecordsCollection = collection(db, 'fleet_service_records');

// The new store (USE_CONTRACTS_V2, owner 27 Sep 2026: "we will not rely on Firebase anymore"): the inventory and the
// service records are the engine's (engine/api/fleet_routes.py /api/store/service/...), each {id, ...fields, version}.
// A new record gets its reference number (YELO-yy/mm/####) from the engine in the same step that takes its parts from
// stock; an edit / an inventory change names the `version` it was opened with (409 record-changed: nothing saved,
// {success: false, code: 'record-changed', error}); there are no live listeners - the page reads again after a save.
const viaStore = async (call) => {
    try {
        return await call();
    } catch (e) {
        throw storeError(e);
    }
};
const refused = (what, error) => {
    logger.error(what, error);
    return { success: false, error: error.message, code: error.code };
};
const NOT_ON_STORE = (what) => ({ success: false, error: `${what} is not available on the new store.` });

/**
 * Inside a transaction: read the inventory items a stock change touches (reads must come before
 * writes). A part taken from stock must still exist; a part given back whose item was deleted
 * from the inventory since has nowhere to go and is skipped (the record change still goes through).
 */
const readStock = async (tx, deltas) => {
    const ids = [...deltas.keys()];
    const snaps = await Promise.all(ids.map((itemId) => tx.get(doc(inventoryCollection, itemId))));
    return ids.map((itemId, i) => {
        const amount = deltas.get(itemId);
        if (!snaps[i].exists()) {
            if (amount < 0) throw new Error(`Inventory item ${itemId} no longer exists.`);
            return null;
        }
        return { ref: snaps[i].ref, amount };
    }).filter(Boolean);
};

const writeStock = (tx, stock, now) => {
    stock.forEach(({ ref, amount }) => tx.update(ref, { quantity: increment(amount), last_updated: now }));
};

export const fleetServiceHistoryService = {
    // Inventory Operations
    addInventoryItem: async (item) => {
        if (USE_CONTRACTS_V2) {
            try {
                const saved = await viaStore(() => finesService.storeServiceInventoryAdd(writableFields(item)));
                return { success: true, ...saved };
            } catch (error) {
                return refused('Error adding inventory item:', error);
            }
        }
        try {
            const docRef = doc(inventoryCollection);
            const newItem = {
                ...item,
                quantity: Number(item.quantity) || 0,
                minStock: Number(item.minStock) || 0,
                created_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            };
            await setDoc(docRef, newItem);
            return { success: true, id: docRef.id, ...newItem };
        } catch (error) {
            logger.error('Error adding inventory item:', error);
            return { success: false, error: error.message };
        }
    },

    // `version` (the store): the version of the item the form opened (default: the item's own `version`)
    updateInventoryItem: async (id, item, { version } = {}) => {
        if (USE_CONTRACTS_V2) {
            try {
                const saved = await viaStore(() => finesService.storeServiceInventorySave(id, writableFields(item), version ?? item?.version));
                return { success: true, item: saved };
            } catch (error) {
                return refused('Error updating inventory item:', error);
            }
        }
        try {
            const docRef = doc(inventoryCollection, id);
            await updateDoc(docRef, {
                ...item,
                quantity: Number(item.quantity) || 0,
                minStock: Number(item.minStock) || 0,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error updating inventory item:', error);
            return { success: false, error: error.message };
        }
    },

    adjustInventoryQuantity: async (id, amount) => {
        // the store: stock moves only with the service records (one transaction on the engine); nothing calls this
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('Adjusting a quantity alone');
        try {
            const docRef = doc(inventoryCollection, id);
            await updateDoc(docRef, {
                quantity: increment(Number(amount)),
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error adjusting inventory quantity:', error);
            return { success: false, error: error.message };
        }
    },

    subscribeToInventory: (callback) => {
        // the store has no live listener: one read (re-read after each save)
        if (USE_CONTRACTS_V2) {
            fleetServiceHistoryService.getInventory().then(callback);
            return () => {};
        }
        const unsubscribe = onSnapshot(inventoryCollection, (snapshot) => {
            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(inventoryData);
        }, (error) => {
            logger.error('Error subscribing to inventory:', error);
        });
        return unsubscribe;
    },

    fixInventoryDataTypes: async () => {
        // the store writes quantity / minStock as numbers itself; nothing calls this
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('The inventory type repair');
        try {
            logger.info('[Data Fix] Starting inventory data type repair...');
            const snapshot = await getDocs(inventoryCollection);
            const updates = snapshot.docs.map(async (docSnapshot) => {
                const data = docSnapshot.data();
                
                // Check if needs fixing (is string)
                if (typeof data.quantity === 'string' || typeof data.minStock === 'string') {
                    logger.info(`[Data Fix] Fixing item "${data.name}" (ID: ${docSnapshot.id})...`);
                    logger.info(`[Data Fix] Old Qty: ${data.quantity} (${typeof data.quantity}), Old Min: ${data.minStock} (${typeof data.minStock})`);
                    
                    const newQty = Number(data.quantity) || 0;
                    const newMin = Number(data.minStock) || 0;
                    const docRef = doc(inventoryCollection, docSnapshot.id);
                    
                    await updateDoc(docRef, {
                        quantity: newQty,
                        minStock: newMin,
                        last_updated: new Date().toISOString()
                    });
                    logger.info(`[Data Fix] Fixed: Qty -> ${newQty}, Min -> ${newMin}`);
                    return true;
                }
                return false;
            });
            
            const results = await Promise.all(updates);
            const fixedCount = results.filter(Boolean).length;
            logger.info(`[Data Fix] Complete. Fixed ${fixedCount} items.`);
            return { success: true, fixedCount };
        } catch (error) {
            logger.error('[Data Fix] Error:', error);
            logger.error('Error fixing inventory types:', error);
            return { success: false, error: error.message };
        }
    },

    getInventory: async () => {
        try {
            if (USE_CONTRACTS_V2) return (await viaStore(() => finesService.storeServiceInventory()))?.items || [];
            const snapshot = await getDocs(inventoryCollection);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error('Error fetching inventory:', error);
            return [];
        }
    },

    // `version` (the store): when given, an item changed since (a service took parts from it) is not deleted
    deleteInventoryItem: async (id, { version } = {}) => {
        if (USE_CONTRACTS_V2) {
            try {
                await viaStore(() => finesService.storeServiceInventoryDelete(id, version));
                return { success: true };
            } catch (error) {
                return refused('Error deleting inventory item:', error);
            }
        }
        try {
            await deleteDoc(doc(inventoryCollection, id));
            return { success: true };
        } catch (error) {
            logger.error('Error deleting inventory item:', error);
            return { success: false, error: error.message };
        }
    },

    // Service Records Operations
    addServiceRecord: async (record) => {
        // the store saves a record only with its stock (saveServiceRecordWithStock); nothing calls this
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('Saving a service record without its stock');
        try {
            const docRef = doc(serviceRecordsCollection);
            const newRecord = {
                ...record,
                created_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            };
            await setDoc(docRef, newRecord);
            return { success: true, id: docRef.id, ...newRecord };
        } catch (error) {
            logger.error('Error adding service record:', error);
            return { success: false, error: error.message };
        }
    },

    updateServiceRecord: async (id, record) => {
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('Saving a service record without its stock');
        try {
            const docRef = doc(serviceRecordsCollection, id);
            await updateDoc(docRef, {
                ...record,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error('Error updating service record:', error);
            return { success: false, error: error.message };
        }
    },

    subscribeToServiceRecords: (callback) => {
        if (USE_CONTRACTS_V2) {
            fleetServiceHistoryService.getServiceRecords().then(callback);
            return () => {};
        }
        const unsubscribe = onSnapshot(serviceRecordsCollection, (snapshot) => {
            const recordsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(recordsData);
        }, (error) => {
            logger.error('Error subscribing to service records:', error);
        });
        return unsubscribe;
    },

    getServiceRecords: async () => {
        try {
            if (USE_CONTRACTS_V2) return (await viaStore(() => finesService.storeServiceRecords()))?.items || [];
            const snapshot = await getDocs(serviceRecordsCollection);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error('Error fetching service records:', error);
            return [];
        }
    },

    /**
     * Save a service record AND move its parts in or out of stock in one transaction: all of it is
     * written or none. Before, stock was changed first and the record after, with the stock results
     * unchecked, so a refused record write left the stock wrong; and a delete that failed after
     * returning the parts returned them again on the retry (audit FLT-19).
     * `id` null = a new record. The parts given back on an edit are the ones SAVED on the record
     * (read inside the transaction), not the page's copy. Returns { success, id } / { success: false, error }.
     * The store (USE_CONTRACTS_V2): the engine does the same in one transaction - a new record also gets its
     * reference number there (any referenceNumber sent is ignored); an edit names `version` (default the record's
     * own); answers { success, id, record } (the record as saved: referenceNumber, version).
     */
    saveServiceRecordWithStock: async (id, record, { version } = {}) => {
        if (USE_CONTRACTS_V2) {
            try {
                const fields = writableFields(record);
                const saved = id
                    ? await viaStore(() => finesService.storeServiceRecordSave(id, fields, version ?? record?.version))
                    : await viaStore(() => finesService.storeServiceRecordAdd(fields));
                return { success: true, id: saved?.id ?? id, record: saved };
            } catch (error) {
                return refused('Error saving service record with stock:', error);
            }
        }
        try {
            const recordRef = id ? doc(serviceRecordsCollection, id) : doc(serviceRecordsCollection);
            await runTransaction(db, async (tx) => {
                const savedSnap = id ? await tx.get(recordRef) : null;
                if (id && !savedSnap.exists()) throw new Error('This service record no longer exists.');
                const oldItems = savedSnap ? (savedSnap.data().items || []) : [];
                const stock = await readStock(tx, stockDeltas(oldItems, record.items || []));
                const now = new Date().toISOString();
                if (id) tx.update(recordRef, { ...record, last_updated: now });
                else tx.set(recordRef, { ...record, created_at: now, last_updated: now });
                writeStock(tx, stock, now);
            });
            return { success: true, id: recordRef.id };
        } catch (error) {
            logger.error('Error saving service record with stock:', error);
            return { success: false, error: error.message };
        }
    },

    /** Delete a service record and give its parts back to stock, in one transaction (see above). */
    deleteServiceRecordWithStock: async (id) => {
        if (USE_CONTRACTS_V2) {
            try {
                await viaStore(() => finesService.storeServiceRecordDelete(id));   // already deleted: nothing given back twice
                return { success: true };
            } catch (error) {
                return refused('Error deleting service record with stock:', error);
            }
        }
        try {
            const recordRef = doc(serviceRecordsCollection, id);
            await runTransaction(db, async (tx) => {
                const savedSnap = await tx.get(recordRef);
                if (!savedSnap.exists()) return;   // already deleted: nothing to give back twice
                const stock = await readStock(tx, stockDeltas(savedSnap.data().items || [], []));
                tx.delete(recordRef);
                writeStock(tx, stock, new Date().toISOString());
            });
            return { success: true };
        } catch (error) {
            logger.error('Error deleting service record with stock:', error);
            return { success: false, error: error.message };
        }
    },

    deleteServiceRecord: async (id) => {
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('Deleting a service record without its stock');
        try {
            await deleteDoc(doc(serviceRecordsCollection, id));
            return { success: true };
        } catch (error) {
            logger.error('Error deleting service record:', error);
            return { success: false, error: error.message };
        }
    },

    // Atomically issues the next `YELO-yy/mm/####` reference number.
    // NOTE: Firestore rule for `settings/service_reference_counter` must allow write for
    // users holding the `add_service_record` permission (or admins) - see firestore.rules.
    nextReferenceNumber: async () => {
        // the store: the engine issues the number when it saves the record (saveServiceRecordWithStock)
        if (USE_CONTRACTS_V2) throw new Error('The reference number is issued by the engine when the service record is saved.');
        const counterRef = doc(db, 'settings', 'service_reference_counter');
        const now = new Date();
        const yy = now.getFullYear().toString().slice(-2);
        const mm = (now.getMonth() + 1).toString().padStart(2, '0');
        const period = `${yy}/${mm}`;

        const seq = await runTransaction(db, async (transaction) => {
            const snapshot = await transaction.get(counterRef);

            let nextSeq;
            if (!snapshot.exists()) {
                // One-time seed: continue numbering from today's highest existing
                // sequence for this period instead of starting over at 0001.
                const prefix = `YELO-${period}/`;
                const recordsSnapshot = await getDocs(serviceRecordsCollection);
                const existingSequences = recordsSnapshot.docs
                    .map(d => d.data().referenceNumber)
                    .filter(ref => ref && ref.startsWith(prefix))
                    .map(ref => parseInt(ref.split('/').pop(), 10))
                    .filter(n => !isNaN(n));
                const maxSequence = existingSequences.length > 0 ? Math.max(...existingSequences) : 0;
                nextSeq = maxSequence + 1;
            } else {
                const data = snapshot.data();
                nextSeq = data.period === period ? (Number(data.seq) || 0) + 1 : 1;
            }

            transaction.set(counterRef, {
                period,
                seq: nextSeq,
                last_updated: new Date().toISOString()
            });

            return nextSeq;
        });

        return `YELO-${period}/${String(seq).padStart(4, '0')}`;
    },

    fixDuplicateReferenceNumbers: async () => {
        // the store issues every number once, from one sequence: nothing to repair (the page hides the button)
        if (USE_CONTRACTS_V2) return NOT_ON_STORE('Repairing reference numbers');
        try {
            logger.info('[Data Fix] Scanning for duplicate reference numbers...');
            const snapshot = await getDocs(serviceRecordsCollection);
            const records = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Group by Ref No
            const groups = {};
            records.forEach(r => {
                const ref = r.referenceNumber;
                if (!ref) return;
                if (!groups[ref]) groups[ref] = [];
                groups[ref].push(r);
            });

            let fixedCount = 0;
            for (const ref in groups) {
                if (groups[ref].length > 1) {
                    logger.info(`[Data Fix] Found ${groups[ref].length} records with Ref No: ${ref}`);
                    
                    // Sort by creation date (if available) or ID
                    const sorted = groups[ref].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
                    
                    // Keep the first one, update others
                    for (let i = 1; i < sorted.length; i++) {
                        const record = sorted[i];
                        const parts = ref.split('/');
                        const prefix = parts.slice(0, -1).join('/') + '/';
                        
                        // Find next available sequence for that month
                        const currentMonthPrefix = prefix;
                        const sameMonthRecords = records.filter(r => r.referenceNumber && r.referenceNumber.startsWith(currentMonthPrefix));
                        const sequences = sameMonthRecords.map(r => parseInt(r.referenceNumber.split('/').pop())).filter(n => !isNaN(n));
                        const maxSeq = sequences.length > 0 ? Math.max(...sequences) : 0;
                        const newRef = `${currentMonthPrefix}${(maxSeq + 1).toString().padStart(4, '0')}`;
                        
                        logger.info(`[Data Fix] Updating record ${record.id}: ${ref} -> ${newRef}`);
                        await updateDoc(doc(serviceRecordsCollection, record.id), {
                            referenceNumber: newRef,
                            last_updated: new Date().toISOString()
                        });
                        
                        // Update our local tracking to avoid picking the same newRef again in next iteration
                        record.referenceNumber = newRef;
                        fixedCount++;
                    }
                }
            }
            logger.info(`[Data Fix] Finished. Fixed ${fixedCount} duplicates.`);
            return { success: true, fixedCount };
        } catch (error) {
            logger.error('[Data Fix] Error fixing duplicates:', error);
            return { success: false, error: error.message };
        }
    }
};
